import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useEstabsContext } from '../../../contexts/EstabsContext';
import { formatBRL } from '../../agenda/cliente/List/helpers';
import { ServicosService } from '../../servicos/Service';
import { EstoqueService } from '../Service';
import { formatQuantity } from '../units';
import { Calendar, ChartColumn, TriangleAlert } from 'lucide-react';

const estoqueService = new EstoqueService();
const servicosService = new ServicosService();

export default function EstoqueReports() {
    const { estabSession } = useEstabsContext();
    const [services, setServices] = useState<any[]>([]);
    const [items, setItems] = useState<any[]>([]);
    const [startAt, setStartAt] = useState(() => moment().subtract(7, 'days').format('YYYY-MM-DD'));
    const [endAt, setEndAt] = useState(() => moment().format('YYYY-MM-DD'));
    const [servicoId, setServicoId] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [data, setData] = useState<any>(null);

    useEffect(() => {
        const loadOptions = async () => {
            if (!estabSession?._id) return;
            try {
                const [servicesResp, itemsResp] = await Promise.all([
                    servicosService.getByEstabId(estabSession._id).then((r) => r?.data || []),
                    estoqueService.findItems({ limit: 200, page: 1 }).then((r) => r?.data || {}),
                ]);
                setServices(servicesResp || []);
                setItems(itemsResp?.data || []);
            } catch (e) {
                console.error(e);
            }
        };
        void loadOptions();
    }, [estabSession?._id]);

    const serviceById = useMemo(
        () =>
            services.reduce((acc: any, service: any) => {
                acc[service._id] = service;
                return acc;
            }, {}),
        [services],
    );

    const itemById = useMemo(
        () =>
            items.reduce((acc: any, item: any) => {
                acc[item._id] = item;
                return acc;
            }, {}),
        [items],
    );

    const periodInvalid = !startAt || !endAt || startAt > endAt;

    useEffect(() => {
        if (periodInvalid) return;
        let ignore = false;

        const search = async () => {
            setLoading(true);
            setError(false);
            try {
                const result = await estoqueService
                    .getRelatorioConsumo({
                        startAt: new Date(`${startAt}T00:00:00`).toISOString(),
                        endAt: new Date(`${endAt}T23:59:59`).toISOString(),
                        servicoId: servicoId || undefined,
                    })
                    .then((r) => r?.data || null);
                if (!ignore) setData(result);
            } catch (e) {
                console.error(e);
                if (!ignore) setError(true);
            } finally {
                if (!ignore) setLoading(false);
            }
        };

        void search();
        return () => {
            ignore = true;
        };
    }, [startAt, endAt, servicoId, periodInvalid]);

    const byItem: any[] = data?.byItem || [];
    const byService: any[] = data?.byService || [];
    const totalCost = byItem.reduce((sum, row) => sum + (Number(row.totalCost) || 0), 0);
    const itemLabel = (itemId: string) => itemById[itemId]?.name || 'Item removido';

    return (
        <div className="space-y-6">
            <div className="sf-toolbar-row !mb-0">
                <label className="flex items-center gap-2 text-sm text-base-content/70">
                    De
                    <input
                        type="date"
                        className="sf-select-field"
                        value={startAt}
                        max={endAt}
                        onChange={(e) => setStartAt(e.target.value)}
                    />
                </label>
                <label className="flex items-center gap-2 text-sm text-base-content/70">
                    Até
                    <input
                        type="date"
                        className="sf-select-field"
                        value={endAt}
                        min={startAt}
                        onChange={(e) => setEndAt(e.target.value)}
                    />
                </label>
                <select
                    className="sf-select-field"
                    value={servicoId}
                    onChange={(e) => setServicoId(e.target.value)}
                    aria-label="Serviço"
                >
                    <option value="">Todos os serviços</option>
                    {services.map((service) => (
                        <option key={service._id} value={service._id}>
                            {service.name}
                        </option>
                    ))}
                </select>
            </div>

            {periodInvalid ? (
                <div className="sf-state">
                    <Calendar aria-hidden />
                    <h2>Escolha um período válido</h2>
                </div>
            ) : loading ? (
                <div className="space-y-2" aria-busy="true">
                    <div className="sf-skeleton h-16" />
                    <div className="sf-skeleton h-32" />
                </div>
            ) : error ? (
                <div className="sf-state">
                    <TriangleAlert aria-hidden />
                    <h2>Não foi possível gerar o relatório</h2>
                    <p>Ajuste o período ou o serviço para tentar de novo.</p>
                </div>
            ) : !byItem.length && !byService.length ? (
                <div className="sf-state">
                    <ChartColumn aria-hidden />
                    <h2>Sem consumo no período</h2>
                    <p>O consumo aparece quando atendimentos de serviços com receita são concluídos.</p>
                </div>
            ) : (
                <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
                    <section>
                        <h2 className="sf-group-title">Por item · custo total {formatBRL(totalCost)}</h2>
                        <ul className="sf-group">
                            {byItem.map((row) => (
                                <li key={row.itemId}>
                                    <div className="sf-row">
                                        <span className="sf-row-body">
                                            <span className="sf-row-title">{itemLabel(row.itemId)}</span>
                                            <span className="sf-row-sub">
                                                {formatQuantity(row.totalQuantity)} {itemById[row.itemId]?.unit}
                                            </span>
                                        </span>
                                        <span className="sf-row-end">{formatBRL(Number(row.totalCost) || 0)}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section>
                        <h2 className="sf-group-title">Por serviço</h2>
                        <ul className="sf-group">
                            {byService.map((row) => (
                                <li key={`${row.servicoId}-${row.itemId}`}>
                                    <div className="sf-row">
                                        <span className="sf-row-body">
                                            <span className="sf-row-title">
                                                {serviceById[row.servicoId]?.name || 'Serviço indisponível'}
                                            </span>
                                            <span className="sf-row-sub">
                                                {itemLabel(row.itemId)} · {formatQuantity(row.totalQuantity)} {itemById[row.itemId]?.unit}
                                            </span>
                                        </span>
                                        <span className="sf-row-end">{formatBRL(Number(row.totalCost) || 0)}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </section>
                </div>
            )}
        </div>
    );
}
